/**
 * Script library popover: the person's saved replies (shared with the CRM chat
 * window). Click a script to put it in the draft, or send it right away.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatherIcon } from '../components/FeatherIcon';
import { LoadingBlock, Spinner } from '../components/Spinner';
import { loadLibrary, newId, saveLibrary, type Script, type ScriptLibraryData } from './scripts';

const ALL = '__all__';

interface Props {
  accountId: string;
  onInsert: (text: string) => void;
  /** Send `text` as its own message; resolves true when it went out. */
  onSend: (text: string) => Promise<boolean>;
  onClose: () => void;
}

export function ScriptLibrary({ accountId, onInsert, onSend, onClose }: Props) {
  const { t } = useTranslation('chat');
  const [library, setLibrary] = useState<ScriptLibraryData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [folder, setFolder] = useState(ALL);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<Script | null>(null);
  const [newFolder, setNewFolder] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [sending, setSending] = useState<string | null>(null);
  const search = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let live = true;
    loadLibrary(accountId)
      .then((lib) => live && setLibrary(lib))
      .catch((err) => live && setError((err as Error).message));
    search.current?.focus();
    return () => {
      live = false;
    };
  }, [accountId]);

  const commit = (next: ScriptLibraryData) => {
    setLibrary(next);
    setSaveError(null);
    saveLibrary(accountId, next).catch((err) => setSaveError((err as Error).message));
  };

  const shown = useMemo(() => {
    if (!library) return [];
    const q = query.trim().toLowerCase();
    return library.scripts.filter(
      (s) => (q || folder === ALL || s.folderId === folder) && (!q || s.title.toLowerCase().includes(q) || s.text.toLowerCase().includes(q)),
    );
  }, [library, folder, query]);

  if (editing && library) {
    const save = (e: FormEvent) => {
      e.preventDefault();
      if (!editing.title.trim() || !editing.text.trim()) return;
      const clean = { ...editing, title: editing.title.trim(), text: editing.text.trim() };
      const exists = library.scripts.some((s) => s.id === clean.id);
      commit({ ...library, scripts: exists ? library.scripts.map((s) => (s.id === clean.id ? clean : s)) : [...library.scripts, clean] });
      setEditing(null);
    };
    return (
      <form className="rc-pop rc-scripts-pop" onSubmit={save}>
        <div className="rc-pop-title">
          <b>{library.scripts.some((s) => s.id === editing.id) ? t('scripts.edit') : t('scripts.new')}</b>
        </div>
        <label className="rc-field">
          <span>{t('scripts.titleLabel')}</span>
          <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} autoFocus />
        </label>
        <label className="rc-field">
          <span>{t('scripts.folder')}</span>
          <select value={editing.folderId} onChange={(e) => setEditing({ ...editing, folderId: e.target.value })}>
            {library.folders.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </label>
        <label className="rc-field">
          <span>{t('scripts.text')}</span>
          <textarea rows={4} value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} />
        </label>
        <div className="rc-pop-actions">
          <button type="button" className="rc-btn" onClick={() => setEditing(null)}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="rc-btn rc-btn--primary" disabled={!editing.title.trim() || !editing.text.trim()}>
            {t('common.save')}
          </button>
        </div>
      </form>
    );
  }

  return (
    <div className="rc-pop rc-scripts-pop" role="dialog" aria-label={t('scripts.title')}>
      <div className="rc-pop-head">
        <FeatherIcon name="search" size={14} />
        <input ref={search} value={query} placeholder={t('scripts.search')} autoComplete="off" onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Escape' && onClose()} />
        {library && (
          <button
            type="button"
            className="rc-pop-link"
            onClick={() => setEditing({ id: newId('script'), folderId: folder !== ALL ? folder : library.folders[0]!.id, title: '', text: '' })}
          >
            <FeatherIcon name="plus" size={12} />
            {t('scripts.add')}
          </button>
        )}
        <button type="button" className="rc-pop-close" title={t('common.close')} onClick={onClose}>
          <FeatherIcon name="x" size={14} />
        </button>
      </div>
      {!library && !error && <LoadingBlock pad="16px 0" label={t('scripts.loading')} />}
      {error && <div className="rc-pop-empty">{t('scripts.loadFailed', { error })}</div>}
      {library && (
        <>
          <div className="rc-scripts-folders">
            {[{ id: ALL, name: t('scripts.all') }, ...library.folders].map((f) => (
              <span key={f.id} className={`rc-scripts-folder ${folder === f.id ? 'is-on' : ''}`}>
                <button type="button" onClick={() => setFolder(f.id)}>
                  {f.name}
                  <span className="rc-muted">{f.id === ALL ? library.scripts.length : library.scripts.filter((s) => s.folderId === f.id).length}</span>
                </button>
                {f.id !== ALL && folder === f.id && library.folders.length > 1 && (
                  <button
                    type="button"
                    className="rc-scripts-folder-del"
                    title={t('scripts.deleteFolder')}
                    onClick={() => {
                      if (confirmDelete !== `folder:${f.id}`) return setConfirmDelete(`folder:${f.id}`);
                      commit({ folders: library.folders.filter((x) => x.id !== f.id), scripts: library.scripts.filter((s) => s.folderId !== f.id) });
                      setFolder(ALL);
                      setConfirmDelete(null);
                    }}
                  >
                    {confirmDelete === `folder:${f.id}` ? t('scripts.confirmDelete') : <FeatherIcon name="trash-2" size={11} />}
                  </button>
                )}
              </span>
            ))}
            {newFolder == null ? (
              <button type="button" className="rc-scripts-folder rc-scripts-folder--add" onClick={() => setNewFolder('')}>
                <FeatherIcon name="plus" size={11} />
                {t('scripts.folderNew')}
              </button>
            ) : (
              <form
                className="rc-scripts-folder-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  const name = newFolder.trim();
                  if (!name) return;
                  const id = newId('folder');
                  commit({ ...library, folders: [...library.folders, { id, name }] });
                  setFolder(id);
                  setNewFolder(null);
                }}
              >
                <input value={newFolder} autoFocus placeholder={t('scripts.folderName')} onChange={(e) => setNewFolder(e.target.value)} onKeyDown={(e) => e.key === 'Escape' && setNewFolder(null)} />
              </form>
            )}
          </div>
          <div className="rc-scripts-list">
            {shown.map((s) => (
              <div key={s.id} className="rc-script">
                <button type="button" className="rc-script-body" title={t('scripts.insert')} onClick={() => onInsert(s.text)}>
                  <b>{s.title}</b>
                  <span>{s.text}</span>
                </button>
                <div className="rc-script-actions">
                  {confirmDelete === s.id ? (
                    <>
                      <button type="button" onClick={() => setConfirmDelete(null)}>
                        {t('common.cancel')}
                      </button>
                      <button
                        type="button"
                        className="is-danger"
                        onClick={() => {
                          commit({ ...library, scripts: library.scripts.filter((x) => x.id !== s.id) });
                          setConfirmDelete(null);
                        }}
                      >
                        {t('scripts.confirmDelete')}
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" title={t('scripts.copy')} onClick={() => void navigator.clipboard?.writeText(s.text).catch(() => undefined)}>
                        <FeatherIcon name="copy" size={13} />
                      </button>
                      <button type="button" title={t('scripts.edit')} onClick={() => setEditing(s)}>
                        <FeatherIcon name="edit-2" size={13} />
                      </button>
                      <button type="button" title={t('scripts.delete')} onClick={() => setConfirmDelete(s.id)}>
                        <FeatherIcon name="trash-2" size={13} />
                      </button>
                      <button
                        type="button"
                        className="is-send"
                        title={t('scripts.sendNow')}
                        disabled={!!sending}
                        onClick={async () => {
                          setSending(s.id);
                          try {
                            if (await onSend(s.text)) onClose();
                          } finally {
                            setSending(null);
                          }
                        }}
                      >
                        {sending === s.id ? <Spinner size={12} label={t('composer.sending')} /> : <FeatherIcon name="send" size={13} />}
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
            {!shown.length && <div className="rc-pop-empty">{t('scripts.empty')}</div>}
          </div>
          {saveError && <div className="rc-pop-empty rc-error">{t('scripts.saveFailed', { error: saveError })}</div>}
        </>
      )}
    </div>
  );
}
